import { action, ActionType } from '../../action';
import { FocusPosition, FocusIndicatorState, TableProps } from '.';
import { TablePreference } from '../Table/TablePreference';

export const TableActionType = {
  SelectRow: 'row/selectRow',
  SelectRows: 'row/selectRows',
  SelectAll: 'row/selectAll',
  FocusCell: 'cell/focus',
  ActiveCell: 'cell/active',
  PressedCell: 'cell/pressed',
  ScrollPosition: 'table/scrollPosition',
  ClearMeasurementCache: 'table/clearMeasurementCache',
  FocusIndicator: 'cell/focusIndicator',
  FrozenColumn: 'table/frozenColumnCount',
  ResizeColumn: 'column/resizeColumn',
  ClearResizedColumns: 'column/clearResizedColumns',
  FreezeColumn: 'table/freezeColumn',
  OrderedColumnKeys: 'table/orderedColumnKeys',
} as const;

export const actions = {
  TableActionType,

  createSelectRow(payload: {
    index: number;
    selected: boolean;
    selectionType?: 'cell' | 'checkbox' | 'radioButton';
    rowCount?: number;
    clearPrevious?: boolean | 'selectionRange';
    rangeSelection?: boolean;
    rowKey: (rowIndex: number) => string;
    getRowIndex?: (rowKey: string) => number;
    interactive?: boolean;
    hideSelectAll?: boolean;
    columnHeadersVisible?: boolean;
    areRowsVisible?: TableProps['areRowsVisible'];
    isRowDisabled?: TableProps['isRowDisabled'];
    isRowReadOnly?: TableProps['isRowReadOnly'];
    selectionStart?: undefined | number;
  }) {
    return action(TableActionType.SelectRow, payload);
  },
  createSelectedRows(payload: {
    selectedRows: ReadonlySet<string> | undefined | null;
    rowCount: number;
    interactive: boolean;
    hideSelectAll?: boolean;
    columnHeadersVisible?: boolean;
    areRowsVisible?: TableProps['areRowsVisible'];
    isRowDisabled?: TableProps['isRowDisabled'];
    isRowReadOnly?: TableProps['isRowReadOnly'];
    initialSelectedRows?: ReadonlySet<string> | null;
  }) {
    return action(TableActionType.SelectRows, payload);
  },
  createSelectAll(payload: {
    selectAll: boolean | 'mixed';
    interactive?: boolean;
    isRowReadOnly?: TableProps['isRowReadOnly'];
    initialSelectedRows?: ReadonlySet<string> | null;
  }) {
    return action(TableActionType.SelectAll, payload);
  },
  createActiveCell(payload: { cell: FocusPosition | undefined }) {
    return action(TableActionType.ActiveCell, payload);
  },
  createPressedCell(payload: { cell: FocusPosition | undefined; pressRow?: boolean }) {
    return action(TableActionType.PressedCell, payload);
  },
  createFocus(payload: { focusedCell: FocusPosition | undefined }) {
    return action(TableActionType.FocusCell, payload);
  },
  createScrollPosition(payload: NonNullable<TablePreference['scrollPosition']>) {
    return action(TableActionType.ScrollPosition, payload);
  },
  createFocusIndicator(payload: { focusIndicator: FocusIndicatorState | undefined }) {
    return action(TableActionType.FocusIndicator, payload);
  },
  createFrozenColumnCount(payload: { count: number }) {
    return action(TableActionType.FrozenColumn, payload);
  },
  createResizeColumn(payload: { columnKey: string; width: number | undefined }) {
    return action(TableActionType.ResizeColumn, payload);
  },
  createClearResizedColumns() {
    return action(TableActionType.ClearResizedColumns);
  },
  createFreezeColumn(payload: {
    columnKey: string;
    unfreeze?: boolean;
    unmanagedColumnKeys?: ReadonlyArray<string>;
    orderedColumnKeys?: ReadonlyArray<string>;
  }) {
    return action(TableActionType.FreezeColumn, payload);
  },
  createOrderedColumnKeys(payload: { orderedKeys: ReadonlyArray<string> }) {
    return action(TableActionType.OrderedColumnKeys, payload);
  },
  createClearMeasurementCache(payload: { startRowIndex: number; startColumnIndex: number }) {
    return action(TableActionType.ClearMeasurementCache, payload);
  },
} as const;
export type TableAction = ActionType<typeof actions>;
